import { get, isEmpty, merge, round } from 'lodash';
import * as d3 from 'd3';
import { Handle } from './vertical.slider.handle';
import { UserService } from '../../providers/user/user.service';
import {
  Component,
  OnInit,
  ElementRef,
  Input,
  Injectable,
  OnChanges,
  SimpleChanges,
  HostListener,
  Output,
  EventEmitter,
  AfterViewInit
} from '@angular/core';

interface IOptions {
  height: number;
  unit: string;
  precision: number;
  padding: {
    top: number;
    right: number;
    bottom: number;
    left: number;
  };
  inputWidth: number;
}

export interface IHandleOptions extends IHandleMinMax {
  precision: number;
  step: number;
  inputMargin: number;
  icon: string;
  callback: Function;
  padding: {
    left: number;
    width: number;
  };
  readOnly: boolean;
  color?: string;
}

interface IHandleMinMax {
  max: number;
  min: number;
}

interface ISettings extends IOptions {
  domain: Array<number>;
  readOnly: boolean;
  handleIcon: string;
  handles: {
    from: IHandleMinMax;
    to: IHandleMinMax;
  };
}

interface ThresholdValues {
  valueFrom: number;
  valueTo: number;
}

@Component({
  selector: 'vertical-slider',
  templateUrl: './vertical.slider.html',
  styleUrls: ['./vertical.slider.scss']
})
@Injectable()
export class VerticalSliderComponent implements OnInit, OnChanges, AfterViewInit {
  elementRef: ElementRef;
  svgElement: any;
  svg: any = null;
  scale: any = null;
  slider: any = null;
  handles: any = {};
  prevClientWidth: number;
  color: string;

  defaultOptions: IOptions = {
    height: 200,
    unit: '',
    precision: 0,
    padding: {
      top: 12,
      right: 12,
      bottom: 12,
      left: 60
    },
    inputWidth: 80
  };

  @Output() setThreshold: EventEmitter<ThresholdValues> = new EventEmitter<ThresholdValues>();
  @Input() from: number;
  @Input() to: number;
  @Input() options: ISettings;

  constructor(elementRef: ElementRef, private userService: UserService) {
    this.elementRef = elementRef;
  }

  @HostListener('window:resize')
  onResize(): void {
    this.redraw();
  }

  ngOnInit(): void {
    this.svgElement = this.elementRef.nativeElement.getElementsByTagName('svg')[0];
    this.color = this.getColorByTheme();
    this.draw();
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.redraw();
    }, 0);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.from !== get(changes, 'from.previousValue', this.from)) {
      if (this.from < this.settings.domain[1]) {
        this.from = this.settings.domain[1];
      }
      this.setThreshold.emit({ valueFrom: this.from, valueTo: this.to });
    }

    if (this.to !== get(changes, 'to.previousValue', this.to)) {
      if (this.to > this.settings.domain[0]) {
        this.to = this.settings.domain[0];
      }
      this.setThreshold.emit({ valueFrom: this.from, valueTo: this.to });
    }

    if (
      !isEmpty(this.handles) &&
      (this.handles.from.value !== this.from || this.handles.to.value !== this.to)
    ) {
      this.updateHandlesValue();
    }
  }

  getColorByTheme(): string {
    if (this.userService.getThemeFromStorage() === 'dark') {
      return '#E5ECF5';
    } else {
      return '#000';
    }
  }

  get step(): number {
    return 1 / Math.pow(10, this.settings.precision);
  }

  updateHandlesValue(): void {
    this.handles.from.options.max = this.to - this.step;
    this.handles.to.options.min = this.from + this.step;

    this.handles.from.value = this.from;
    this.handles.to.value = this.to;

    this.handles.from.updateHandle(this.step, true);
    this.handles.to.updateHandle(this.step, true);
  }

  get width(): number {
    return this.elementRef.nativeElement.clientWidth;
  }

  get settings(): ISettings {
    return merge({}, this.defaultOptions, this.options);
  }

  get range(): Array<number> {
    return [
      this.settings.padding.top + this.settings.padding.bottom,
      this.settings.height - this.settings.padding.top - this.settings.padding.bottom
    ];
  }

  get readOnly(): boolean {
    return this.settings.readOnly;
  }

  positiveNumbers($event, handle: any): void {
    if (handle.value.value) {
      if (handle.key === 'to') {
        this.to = handle.value.value;
      }
      if (handle.key === 'from') {
        this.from = handle.value.value;
      }
      this.setThreshold.emit({ valueFrom: this.from, valueTo: this.to });
    }
    if ($event.charCode > 57 || $event.charCode < 46) {
      $event.preventDefault();
    }
  }

  draw(): void {
    this.prevClientWidth = this.width;
    this.svg = d3
      .select(this.svgElement)
      .attr('width', this.width)
      .attr('height', this.settings.height);

    this.scale = this.createScale();
    this.slider = this.createSlider();
    this.addYAxis();
    this.addXAxis();

    const handles = this.settings.handles;
    handles.from.max = this.to;
    handles.to.min = this.from;

    this.handles.from = new Handle(
      this.slider,
      this.scale,
      this.from,
      this.getHandleOptions(handles.from)
    );
    this.handles.to = new Handle(
      this.slider,
      this.scale,
      this.to,
      this.getHandleOptions(handles.to)
    );

    this.handles.from.updateHandle(this.step);
    this.handles.to.updateHandle(this.step);
  }

  destroy(): void {
    const node = this.svg.node();
    while (node.firstChild) {
      node.removeChild(node.firstChild);
    }
  }

  redraw(): void {
    this.destroy();
    this.draw();
  }

  getHandleOptions(options: IHandleMinMax): IHandleOptions {
    const defaultOptions = {
      min: this.settings.domain[1],
      max: this.settings.domain[0],
      precision: this.settings.precision,
      step: this.step,
      inputMargin: 0,
      icon: this.settings.handleIcon,
      callback: this.updateLayout.bind(this),
      padding: {
        left: this.settings.padding.right,
        width: this.width - this.settings.padding.left - this.settings.inputWidth
      },
      readOnly: this.readOnly
    };

    return merge({}, defaultOptions, options);
  }

  updateLayout(): void {
    if (!this.handles.from || !this.handles.to) {
      return;
    }

    const valueTo = this.handles.to.value;
    const valueFrom = this.handles.from.value;

    this.setThreshold.emit({
      valueFrom: valueFrom,
      valueTo: valueTo
    });

    this.handles.from.options.max = valueTo - this.step;
    this.handles.to.options.min = valueFrom + this.step;
  }

  createScale(): any {
    const scale = d3.scaleLinear();
    scale.domain(this.settings.domain).range(this.range).clamp(true);

    return scale;
  }

  createSlider(): SVGGElement {
    return this.svg
      .append('g')
      .attr('class', 'slider')
      .attr('transform', 'translate(' + this.settings.padding.left + ',' + 0 + ')');
  }

  addYAxis(): void {
    this.slider
      .append('line')
      .attr('class', 'track')
      .attr('stroke', this.color)
      .attr('y1', this.range[0])
      .attr('y2', this.range[1])
      .select(function () {
        return this.parentNode.appendChild(this.cloneNode(true));
      })
      .attr('class', 'track-inset')
      .select(function () {
        return this.parentNode.appendChild(this.cloneNode(true));
      })
      .attr('class', 'track-overlay');

    this.addYAxisLabels();
  }

  addYAxisLabels(): void {
    const textAnchor = 'text-anchor';
    const labelH = 3;
    let value = this.settings.domain[0];
    this.slider
      .append('text')
      .attr('class', 'labels')
      .attr('stroke', this.color)
      .attr('x', -this.settings.padding.right)
      .attr('y', this.scale(value) + labelH)
      .attr(textAnchor, 'end')
      .attr('font-size', '8pt')
      .text(value + this.settings.unit);

    value = round(this.settings.domain[0] / 2, this.settings.precision);
    this.slider
      .append('text')
      .attr('class', 'labels')
      .attr('stroke', this.color)
      .attr('x', -this.settings.padding.right)
      .attr('y', this.scale(value) + labelH)
      .attr(textAnchor, 'end')
      .attr('font-size', '8pt')
      .text(value + this.settings.unit);

    value = this.settings.domain[1];
    this.slider
      .append('text')
      .attr('class', 'labels')
      .attr('stroke', this.color)
      .attr('x', -this.settings.padding.right)
      .attr('y', this.scale(value) + labelH)
      .attr(textAnchor, 'end')
      .attr('font-size', '8pt')
      .text(value + this.settings.unit);
  }

  addXAxis(): void {
    this.slider
      .append('line')
      .attr('class', 'track')
      .attr('stroke', this.color)
      .attr('x1', 0)
      .attr('x2', this.width - this.settings.padding.left - this.settings.inputWidth)
      .attr('y1', this.settings.height - this.settings.padding.top - this.settings.padding.bottom)
      .attr('y2', this.settings.height - this.settings.padding.top - this.settings.padding.bottom);
  }
}
